/**
 * Track 数据注册表
 * 职责：管理 Track 组件的数据注册，支持通过 track-id 查找数据
 */

import type { EnrichOptions } from "../enricher/types";

export interface TrackConfig {
  event: string;
  data: React.RefObject<Record<string, any>>;
  autoEnrich: boolean;
  enrichOptions?: EnrichOptions;
}

export class TrackDataRegistry {
  private registry: Map<string, TrackConfig> = new Map();
  private elementToTrackId: WeakMap<Element, string> = new WeakMap();

  /**
   * 注册 Track 数据
   */
  register(trackId: string, config: TrackConfig): void {
    this.registry.set(trackId, config);

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[TrackDataRegistry] Registered track-id: ${trackId}`,
        config.event
      );
    }
  }

  /**
   * 注销 Track 数据
   */
  unregister(trackId: string): void {
    this.registry.delete(trackId);

    if (process.env.NODE_ENV === "development") {
      console.log(`[TrackDataRegistry] Unregistered track-id: ${trackId}`);
    }
  }

  /**
   * 获取 Track 配置
   */
  get(trackId: string): TrackConfig | null {
    const config = this.registry.get(trackId);
    if (!config) return null;

    // 返回副本，避免外部修改
    return {
      ...config,
      data: config.data, // 保留引用，因为是 RefObject
    };
  }

  /**
   * 查找元素的 track-id
   * 从目标元素开始向上遍历 DOM 树，查找最近的 data-track-id 属性
   */
  findTrackId(node: Element): string | null {
    let current: Element | null = node;

    while (current && current !== document.body) {
      // 检查缓存
      const cached = this.elementToTrackId.get(current);
      if (cached) {
        return cached;
      }

      // 查找属性
      const trackId = current.getAttribute("data-track-id");
      if (trackId) {
        this.elementToTrackId.set(current, trackId);
        return trackId;
      }

      current = current.parentElement;
    }

    return null;
  }

  /**
   * 清除所有注册数据（用于测试）
   */
  clear(): void {
    this.registry.clear();

    if (process.env.NODE_ENV === "development") {
      console.log("[TrackDataRegistry] Cleared all registrations");
    }
  }

  /**
   * 获取注册数量
   */
  getSize(): number {
    return this.registry.size;
  }
}

// 导出单例
export const trackDataRegistry = new TrackDataRegistry();
